import type { AssetFamily } from './asset-family.model';

/** Mode de gestion d'une catégorie — jeu fermé (RM-PIN-123). */
export type ManagementMode = 'exemplaire' | 'quantite';

export interface Category {
  readonly label: string;
  readonly mode: ManagementMode;
  /** Numéro de série obligatoire pour les catégories déclarées sérialisées (RM-PIN-121). */
  readonly serialized: boolean;
  readonly family: AssetFamily;
}

/**
 * Référentiel des catégories, repris de **PJ-06 de SPEC-PIN-001** — la répartition par défaut y est
 * inscrite directement dans la spec, avec le mode de gestion et la sérialisation de chacune.
 *
 * ⚠️ PJ-06 porte la mention « répartition par défaut proposée, **à valider par l'équipe IT** ».
 * Une fois des données saisies, changer le mode de gestion d'une catégorie exige une migration
 * assistée (RM-PIN-200) : mieux vaut valider avant la mise en service.
 *
 * ⚠️ La colonne `family` n'est **pas** dans la spec. Aucun document ne définit le mapping
 * catégorie → famille (PJ-03 de SPEC-PIN-008 est en TODO), alors que deux choses en dépendent :
 * les compteurs d'appareils de l'écran « Vos employés » et la règle de repli du statut
 * d'équipement (RM-PIN-194). Les valeurs ci-dessous sont une proposition à confirmer.
 */
export const CATEGORIES: readonly Category[] = [
  // --- Suivi à l'exemplaire, sérialisées -----------------------------------
  { label: 'PC portable', mode: 'exemplaire', serialized: true, family: 'poste de travail' },
  { label: 'PC fixe', mode: 'exemplaire', serialized: true, family: 'poste de travail' },
  { label: 'Smartphone', mode: 'exemplaire', serialized: true, family: 'mobile' },
  { label: 'Tablette', mode: 'exemplaire', serialized: true, family: 'mobile' },
  { label: 'Écran', mode: 'exemplaire', serialized: true, family: 'écran' },
  { label: 'Téléphone', mode: 'exemplaire', serialized: true, family: 'autre' },

  // --- Suivi à l'exemplaire, numéro de série facultatif --------------------
  { label: "Station d'accueil", mode: 'exemplaire', serialized: false, family: 'périphérique' },
  { label: 'Casque audio professionnel', mode: 'exemplaire', serialized: false, family: 'périphérique' },
  { label: 'Clavier haut de gamme', mode: 'exemplaire', serialized: false, family: 'périphérique' },
  { label: 'Souris / touchpad haut de gamme', mode: 'exemplaire', serialized: false, family: 'périphérique' },

  // --- Suivi en quantité : consommables, pas de fiche matériel -------------
  { label: 'Câble', mode: 'quantite', serialized: false, family: 'autre' },
  { label: 'Adaptateur', mode: 'quantite', serialized: false, family: 'autre' },
  { label: 'Chargeur', mode: 'quantite', serialized: false, family: 'autre' },
  { label: "Micro-casque filaire d'entrée de gamme", mode: 'quantite', serialized: false, family: 'autre' },
  { label: "Clavier d'entrée de gamme", mode: 'quantite', serialized: false, family: 'autre' },
  { label: "Souris d'entrée de gamme", mode: 'quantite', serialized: false, family: 'autre' },
  { label: 'Sacoche', mode: 'quantite', serialized: false, family: 'autre' },
  { label: 'Autres consommables', mode: 'quantite', serialized: false, family: 'autre' },
];

/**
 * Catégories pouvant donner lieu à une fiche matériel. Une catégorie en mode « quantité » produit
 * une référence de consommable suivie en stock, sans numéro d'inventaire ni affectation nominative
 * (RM-PIN-123) : elle n'a donc pas sa place dans ce formulaire.
 */
export const TRACKED_CATEGORIES: readonly Category[] = CATEGORIES.filter(
  (category) => category.mode === 'exemplaire',
);

export function findCategory(label: string): Category | undefined {
  return CATEGORIES.find((category) => category.label === label);
}

/** Vrai si la catégorie exige un numéro de série (RM-PIN-121). */
export function requiresSerialNumber(label: string): boolean {
  return findCategory(label)?.serialized ?? false;
}

export function familyOf(label: string): AssetFamily {
  return findCategory(label)?.family ?? 'autre';
}
